import {Autocomplete, Stack, TextField, Typography} from "@mui/material";
import React from "react";
import {useAuthState} from "react-firebase-hooks/auth";
import {useList} from "react-firebase-hooks/database";
import {
	  auth,
	  Business,
	  createBusinessesHookQuery,
	  createExpensesHookQuery,
	  createMilestonesHookQuery,
	  createPricingsHookQuery,
	  createProductsHookQuery,
	  defView,
	  Expense,
	  Milestone,
	  Pricing,
	  Product,
	  views
} from "../../firebase/index.js";
import {useInMemoryIndustry} from "../../redux/InMemoryIndustryReducer.js";
import type {APXBaseOption, APXOptions} from "../../reusables/APXMoreMenuWithPopover.jsx";
import APXMoreMenuWithPopover from "../../reusables/APXMoreMenuWithPopover.jsx";
import APXQuotesFetcher from "../../reusables/APXQuotesFetcher.jsx";
import {industryColour} from "../../reusables/js/APXTheme.js";
import useHookForPlusUserChecking from "../../reusables/plus/APXPlus.js";
import {APXPlusStatusAlert} from "../../reusables/plus/APXPlusAlerts.jsx";
import AddNewUnitBSL from "./AddNewUnitBSL.jsx";
import AnalyticsLayout from "./analytics/AnalyticsLayout.jsx";
import ExpensesLayout from "./expenses/ExpensesLayout.jsx";
import MilestonesLayout from "./milestones/MilestonesLayout.jsx";
import ProductsLayout from "./products/ProductsLayout.jsx";


export default function IndustryLayout({onIndustryChange}: { onIndustryChange: () => void }) {
	  
	  const inMemoryIndustry = useInMemoryIndustry();
	  
	  const uid = useAuthState(auth)[0]?.uid;
	  
	  const menuOptions: APXOptions = {
			 changeCurrentIndustry: {
					label: "Change Industry",
					needsConfirmation: false,
					keepDisabled: false,
					onClick: () => {
						  onIndustryChange();
					}
			 },
			 addUnit: {
					label: "Add Unit",
					needsConfirmation: false,
					keepDisabled: false,
					onClick: () => {
						  setOpenNewUnitBSL(true);
					}
			 }
	  };
	  
	  const [openNewUnitBSL, setOpenNewUnitBSL] = React.useState(false);
	  
	  const [productSnapshots] = useList(createProductsHookQuery(uid, inMemoryIndustry?.industryKey));
	  const [pricingSnapshots] = useList(createPricingsHookQuery(uid, inMemoryIndustry?.industryKey));
	  const [businessSnapshots] = useList(createBusinessesHookQuery(uid, inMemoryIndustry?.industryKey));
	  const [milestoneSnapshots] = useList(createMilestonesHookQuery(uid, inMemoryIndustry?.industryKey));
	  const [expenseSnapshots] = useList(createExpensesHookQuery(uid, inMemoryIndustry?.industryKey));
	  
	  const [view, setView] = React.useState(defView);
	  
	  const {isPlus} = useHookForPlusUserChecking(uid);
	  
	  return (
				 <>
						{
							  <APXPlusStatusAlert isPlus={isPlus}/>
						}
						{
							  <APXQuotesFetcher/>
						}
						{/*TRUTHY->*/ !!inMemoryIndustry &&
								  <Stack spacing={2} p={1} alignItems={"center"} justifyContent={"center"}>
										 <Stack direction={"row"} spacing={2} p={2} sx={{width: "100%"}} alignItems={"center"} justifyContent={"space-between"}>
												<Typography variant={"h4"} color={industryColour}>{inMemoryIndustry.industryName}</Typography>
												<APXMoreMenuWithPopover options={menuOptions} onConfirmedOptionSelected={(selectedOption: APXBaseOption) => selectedOption.onClick()}/>
										 </Stack>
										 
										 <ViewChooser onViewChange={(view) => setView(view)}/>
										 
										 <Stack p={2} sx={{width: "100%"}} alignItems={"stretch"} justifyContent={"center"}>
												{view === views.products && productSnapshots && pricingSnapshots && businessSnapshots &&
														  <ProductsLayout uid={uid}
																				i={inMemoryIndustry}
																				ps={productSnapshots.map((snapshot) => Product.fromObject(snapshot.val()))}
																				pps={pricingSnapshots.map((snapshot) => Pricing.fromObject(snapshot.val()))}
																				bs={businessSnapshots.map((snapshot) => Business.fromObject(snapshot.val()))}
														  />
												}
												{view === views.milestones && milestoneSnapshots &&
														  <MilestonesLayout
																	 ms={milestoneSnapshots.map((snapshot) => Milestone.fromObject(snapshot.val()))}
														  />
												}
												{view === views.expenses && expenseSnapshots &&
														  <ExpensesLayout uid={uid} industryKey={inMemoryIndustry.industryKey}
																				exps={expenseSnapshots.map((snapshot) => Expense.fromObject(snapshot.val()))}
														  />
												}
												{view === views.analytics && pricingSnapshots && expenseSnapshots &&
														  <AnalyticsLayout
																	 pps={pricingSnapshots.map((snapshot) => Pricing.fromObject(snapshot.val()))}
																	 exps={expenseSnapshots.map((snapshot) => Expense.fromObject(snapshot.val()))}
														  />
												}
										 </Stack>
										 {openNewUnitBSL &&
													<AddNewUnitBSL uid={uid}
																		i={inMemoryIndustry}
																		onClose={() => setOpenNewUnitBSL(false)}
													/>
										 }
								  </Stack>
						}
				 </>
	  );
}


function ViewChooser({onViewChange}: { onViewChange: (view: string) => void }) {
	  
	  const [view, setView] = React.useState(defView);
	  
	  React.useEffect(() => {
			 view && onViewChange(view);
	  }, [view]);
	  
	  return (
				 <>
						<Autocomplete options={Object.values(views)}
										  fullWidth
										  clearOnEscape={false}
										  disableClearable={true}
										  renderInput={
												 (params) =>
															<TextField {...params} label="Choose View" variant="outlined" fullWidth required={false}
																		  inputProps={{...params.inputProps, readOnly: true}}
															/>
										  }
										  value={view}
										  onChange={(e, newValue: string) => setView(newValue)}
						/>
				 </>
	  );
}
